'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    deleteMedicalSupply,
    getMedicalSuppliesByOrganization,
    HEALTH_ONLY_SUPPLY_TYPES,
    MedicalSupply,
    MEDICAL_SUPPLY_TYPE_LABELS,
    MedicalSupplyType,
    NEGOTIATION_MODALITY_LABELS,
    PRODUCT_UNIT_LABELS,
    toggleMedicalSupplyActive,
} from '@/lib/api/medical-supplies/medical-supplies-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { useHasLoadedOnce } from '@/lib/hooks/use-has-loaded-once'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useCallback, useMemo, useState } from 'react'
import {
    MdAdd,
    MdClose,
    MdDelete,
    MdEdit,
    MdFilterList,
    MdMedicalServices,
    MdSearch,
    MdToggleOff,
    MdToggleOn,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

type TypeTab = 'ALL' | MedicalSupplyType

interface TabOption {
    key: TypeTab
    label: string
}

const TYPE_TABS: TabOption[] = [
    { key: 'ALL', label: 'Todos' },
    { key: 'OPME', label: 'OPME' },
    { key: 'PRODUCT', label: 'Produtos' },
    { key: 'EQUIPMENT', label: 'Equipamentos' },
    { key: 'DISPOSABLE_KIT', label: 'Kits Descartáveis' },
    { key: 'PACKAGE', label: 'Pacotes' },
    { key: 'SURGICAL_MATERIAL', label: 'Mat. Cirúrgico' },
    { key: 'INSTRUMENT', label: 'Instrumental' },
    { key: 'IMPLANT', label: 'Implantes' },
    { key: 'INFECTION_PREVENTION_BUNDLE', label: 'Bundles' },
]

const TYPE_BADGE_CLASS: Record<MedicalSupplyType, string> = {
    OPME: 'badge-secondary',
    PRODUCT: 'badge-info',
    DISPOSABLE_KIT: 'badge-secondary',
    INFECTION_PREVENTION_BUNDLE: 'badge-warning',
    PACKAGE: 'badge-accent',
    SURGICAL_MATERIAL: 'badge-success',
    INSTRUMENT: 'badge-ghost',
    IMPLANT: 'badge-error',
    EQUIPMENT: 'badge-neutral',
}

export default function MedicalSuppliesListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'registrations',
        'medical_supplies'
    )
    const [searchInput, setSearchInput] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [toggling, setToggling] = useState<number | null>(null)
    const [showFilters, setShowFilters] = useState(false)
    const [sortBy, setSortBy] = useState<string>('')
    const [activeTab, setActiveTab] = useState<TypeTab>('ALL')
    const [modalityFilter, setModalityFilter] = useState<string>('')
    const [hospitalFilter, setHospitalFilter] = useState<string>('')

    // Material cirúrgico e bundle de prevenção de infecção não existem fora da
    // saúde. Implante e OPME ficam: implante capilar é implante.
    const isHealth = user?.company?.segment === 'HEALTH'
    const visibleTypeTabs = useMemo(
        () =>
            isHealth
                ? TYPE_TABS
                : TYPE_TABS.filter(
                      tab =>
                          tab.key === 'ALL' ||
                          !HEALTH_ONLY_SUPPLY_TYPES.includes(tab.key)
                  ),
        [isHealth]
    )

    const debouncedSearch = useDebounce(searchInput, 300)

    const sortFilters = useMemo(() => {
        if (sortBy === 'az') return { sortBy: 'name', sortOrder: 'asc' }
        if (sortBy === 'za') return { sortBy: 'name', sortOrder: 'desc' }
        if (sortBy === 'oldest')
            return { sortBy: 'createdAt', sortOrder: 'asc' }
        if (sortBy === 'newest')
            return { sortBy: 'createdAt', sortOrder: 'desc' }
        if (sortBy === 'cost_high')
            return { sortBy: 'costPrice', sortOrder: 'desc' }
        if (sortBy === 'cost_low')
            return { sortBy: 'costPrice', sortOrder: 'asc' }
        return {}
    }, [sortBy])

    const filters = useMemo(
        () => ({
            search: debouncedSearch || undefined,
            type: activeTab !== 'ALL' ? activeTab : undefined,
            negotiationModality: modalityFilter || undefined,
            isHospital:
                hospitalFilter === 'true'
                    ? true
                    : hospitalFilter === 'false'
                      ? false
                      : undefined,
            ...sortFilters,
        }),
        [
            debouncedSearch,
            activeTab,
            modalityFilter,
            hospitalFilter,
            sortFilters,
        ]
    )

    const activeFilterCount = [sortBy, modalityFilter, hospitalFilter].filter(
        Boolean
    ).length

    const fetchFn = useCallback(
        (params: any) => {
            return getMedicalSuppliesByOrganization(
                user!.organizationId!,
                params
            )
        },
        [user?.organizationId]
    )

    const [urlPage, setUrlPage] = useUrlPage()

    const { items, loading, page, limit, total, setPage, reload } =
        usePaginatedPage<MedicalSupply, typeof filters>({
            fetchFn,
            filters,
            limit: 15,
            enabled: !!user?.organizationId,
            page: urlPage,
            onPageChange: setUrlPage,
        })

    const hasLoadedOnce = useHasLoadedOnce(loading)

    const handleDelete = (id: number) => {
        setItemToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!itemToDelete) return
        setDeleting(true)
        try {
            const response = await deleteMedicalSupply(itemToDelete)
            if (response.success) {
                toast.success('Material excluído com sucesso')
                reload()
                setShowDeleteModal(false)
                setItemToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir material')
            }
        } catch {
            toast.error('Erro ao excluir material')
        } finally {
            setDeleting(false)
        }
    }

    const handleToggleActive = async (id: number) => {
        setToggling(id)
        try {
            const response = await toggleMedicalSupplyActive(id)
            if (response.success) {
                toast.success(
                    response.result.active
                        ? 'Material ativado'
                        : 'Material desativado'
                )
                reload()
            } else {
                toast.error(response.error || 'Erro ao alterar status')
            }
        } catch {
            toast.error('Erro ao alterar status')
        } finally {
            setToggling(null)
        }
    }

    const formatPrice = (price?: number | null) => {
        if (!price) return '-'
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(price / 100)
    }

    // Só o PRIMEIRO carregamento troca a página pelo spinner; recarregar mantém
    // o campo de busca montado (ver useHasLoadedOnce).
    if (loading && !hasLoadedOnce) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando materiais médicos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdMedicalServices className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Materiais
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total} {total === 1 ? 'material' : 'materiais'}{' '}
                            cadastrados
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() =>
                            navigate('/registrations/supplies/new')
                        }
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Adicionar</span>
                    </button>
                )}
            </div>

            {/* Type Tabs */}
            <div className="tabs tabs-boxed bg-base-100 border border-base-300/50 p-1 flex-wrap">
                {visibleTypeTabs.map(tab => (
                    <button
                        key={tab.key}
                        className={`tab ${activeTab === tab.key ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, descrição ou código..."
                            className="input input-bordered w-full pl-10"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowFilters(!showFilters)}
                        className={`btn btn-sm ${showFilters || activeFilterCount > 0 ? 'btn-secondary' : 'btn-ghost'} gap-1`}
                    >
                        <MdFilterList className="text-lg" />
                        <span className="hidden sm:inline">Filtros</span>
                        {activeFilterCount > 0 && (
                            <span className="badge badge-sm badge-secondary">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>
                    {searchInput && (
                        <button
                            type="button"
                            onClick={() => setSearchInput('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>

                {showFilters && (
                    <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-base-300/50">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-base-content/60">
                                Ordenar por
                            </label>
                            <select
                                className="select select-bordered select-sm w-40"
                                value={sortBy}
                                onChange={e => setSortBy(e.target.value)}
                            >
                                <option value="">Padrão</option>
                                <option value="az">A-Z</option>
                                <option value="za">Z-A</option>
                                <option value="newest">Mais novos</option>
                                <option value="oldest">Mais antigos</option>
                                <option value="cost_high">Maior custo</option>
                                <option value="cost_low">Menor custo</option>
                            </select>
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-base-content/60">
                                Modalidade
                            </label>
                            <select
                                className="select select-bordered select-sm w-40"
                                value={modalityFilter}
                                onChange={e =>
                                    setModalityFilter(e.target.value)
                                }
                            >
                                <option value="">Todas</option>
                                <option value="SALE">Venda</option>
                                <option value="RENTAL">Aluguel</option>
                            </select>
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-base-content/60">
                                Hospitalar
                            </label>
                            <select
                                className="select select-bordered select-sm w-40"
                                value={hospitalFilter}
                                onChange={e =>
                                    setHospitalFilter(e.target.value)
                                }
                            >
                                <option value="">Todos</option>
                                <option value="true">Sim</option>
                                <option value="false">Não</option>
                            </select>
                        </div>

                        {activeFilterCount > 0 && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSortBy('')
                                    setModalityFilter('')
                                    setHospitalFilter('')
                                }}
                                className="btn btn-ghost btn-sm text-error"
                            >
                                Limpar filtros
                            </button>
                        )}
                    </div>
                )}
            </div>

            {loading && items.length === 0 ? (
                <div className="flex items-center justify-center min-h-[200px] gap-3 bg-base-100 rounded-xl border border-base-300/50">
                    <span className="loading loading-spinner loading-md text-secondary"></span>
                    <span className="text-sm text-base-content/60">
                        Buscando materiais...
                    </span>
                </div>
            ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdMedicalServices className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchInput
                                ? 'Nenhum material encontrado'
                                : 'Nenhum material cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchInput
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando materiais médicos'}
                        </p>
                        {!searchInput && canCreate && (
                            <button
                                onClick={() =>
                                    navigate(
                                        '/registrations/supplies/new'
                                    )
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro material
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div
                    className={`flex flex-col gap-3 transition-opacity ${
                        loading ? 'opacity-50' : ''
                    }`}
                >
                    {items.map(item => (
                        <div
                            key={item.id}
                            className={`group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300 ${!item.active ? 'opacity-60' : ''}`}
                        >
                            <div className="flex items-center gap-4 p-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                        <h3 className="text-base font-semibold text-base-content">
                                            {item.name}
                                        </h3>
                                        <span
                                            className={`badge badge-sm ${TYPE_BADGE_CLASS[item.type]}`}
                                        >
                                            {
                                                MEDICAL_SUPPLY_TYPE_LABELS[
                                                    item.type
                                                ]
                                            }
                                        </span>
                                        <span
                                            className={`badge badge-sm ${item.active ? 'badge-success' : 'badge-ghost'}`}
                                        >
                                            {item.active ? 'Ativo' : 'Inativo'}
                                        </span>
                                        {item.isHospital && (
                                            <span className="badge badge-sm badge-outline badge-info">
                                                Hospitalar
                                            </span>
                                        )}
                                    </div>

                                    {item.description && (
                                        <p className="text-sm text-base-content/70 mb-2 line-clamp-1">
                                            {item.description}
                                        </p>
                                    )}

                                    <div className="flex flex-wrap items-center gap-4 text-sm">
                                        {item.code && (
                                            <span className="badge badge-outline badge-sm">
                                                {item.code}
                                            </span>
                                        )}
                                        <span className="text-base-content/70">
                                            {item.quantity}{' '}
                                            {PRODUCT_UNIT_LABELS[item.unit]}
                                        </span>
                                        {item.costPrice > 0 && (
                                            <span className="text-base-content/70">
                                                Custo:{' '}
                                                {formatPrice(item.costPrice)}
                                            </span>
                                        )}
                                        {item.sellingPrice != null &&
                                            item.sellingPrice > 0 && (
                                                <span className="font-semibold text-secondary">
                                                    Venda:{' '}
                                                    {formatPrice(
                                                        item.sellingPrice
                                                    )}
                                                </span>
                                            )}
                                        <span className="text-base-content/70">
                                            {
                                                NEGOTIATION_MODALITY_LABELS[
                                                    item.negotiationModality
                                                ]
                                            }
                                        </span>
                                        {item.supplier && (
                                            <span className="text-base-content/70">
                                                {item.supplier.name}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-1">
                                    {canUpdate && (
                                        <button
                                            onClick={() =>
                                                handleToggleActive(item.id)
                                            }
                                            className="btn btn-sm btn-ghost"
                                            title={
                                                item.active
                                                    ? 'Desativar'
                                                    : 'Ativar'
                                            }
                                            disabled={toggling === item.id}
                                        >
                                            {toggling === item.id ? (
                                                <span className="loading loading-spinner loading-xs"></span>
                                            ) : item.active ? (
                                                <MdToggleOn className="text-xl text-success" />
                                            ) : (
                                                <MdToggleOff className="text-xl text-base-content/50" />
                                            )}
                                        </button>
                                    )}
                                    {canUpdate && (
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `/registrations/supplies/${item.id}`
                                                )
                                            }
                                            className="btn btn-sm btn-ghost"
                                            title="Editar"
                                        >
                                            <MdEdit className="text-lg" />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            onClick={() =>
                                                handleDelete(item.id)
                                            }
                                            className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                            title="Excluir"
                                        >
                                            <MdDelete className="text-lg" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="suprimento"
                        />
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setItemToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir material"
                message="Tem certeza de que deseja excluir este material? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
